import { ReactNode, useEffect, useState } from 'react'
import { IconButton, iconSizes, Tooltip } from '@common/ui'
import { IconChevronLeft } from '@common/ui/icons'
import { Group, Panel, Separator, useDefaultLayout, usePanelRef } from 'react-resizable-panels'
import classes from './ResizableSplit.module.css'

export type ResizableSplitProps = {
  // Persists the split ratio (and thereby the collapsed state) in localStorage.
  autoSaveId: string
  left: ReactNode
  // Render prop so the right panel can place its own collapse affordance (e.g. in a header row).
  right: (args: { collapse: () => void }) => ReactNode
  expandLabel?: string
  leftMinSize?: string
  rightMinSize?: string
}

/**
 * Horizontal two-pane layout with a draggable divider. The right pane snaps closed when dragged below its minimum
 * size (or via the render-prop `collapse` callback) and reopens from the chevron that replaces it.
 */
const ResizableSplit = ({
  autoSaveId,
  left,
  right,
  expandLabel = 'Show panel',
  leftMinSize = '25%',
  rightMinSize = '20%',
}: ResizableSplitProps) => {
  const rightPanelRef = usePanelRef()
  const [collapsed, setCollapsed] = useState(false)
  const [dragging, setDragging] = useState(false)
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({ id: autoSaveId })

  // The library only reports drag state internally, so track it from the separator's pointer events.
  useEffect(() => {
    if (!dragging) return
    const stop = () => setDragging(false)
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
    return () => {
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
    }
  }, [dragging])

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <Group
        orientation="horizontal"
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
        style={{ flex: 1, minHeight: 0 }}
      >
        <Panel id="left" defaultSize="50%" minSize={leftMinSize}>
          <div style={{ height: '100%', display: 'flex', paddingRight: 16 }}>{left}</div>
        </Panel>
        <Separator onPointerDown={() => setDragging(true)} aria-label="resize panels">
          <div className={classes.handle} data-dragging={dragging ? 'true' : 'false'} />
        </Separator>
        <Panel
          id="right"
          defaultSize="50%"
          minSize={rightMinSize}
          collapsible
          collapsedSize={0}
          panelRef={rightPanelRef}
          // Also fires on mount, syncing `collapsed` with a persisted collapsed layout.
          onResize={(size) => setCollapsed(size.inPixels < 1)}
        >
          <div style={{ height: '100%', display: 'flex', paddingLeft: 16, overflow: 'hidden' }}>
            {right({ collapse: () => rightPanelRef.current?.collapse() })}
          </div>
        </Panel>
      </Group>
      {collapsed && (
        <Tooltip label={expandLabel}>
          <IconButton
            aria-label={expandLabel}
            size="sm"
            onClick={() => rightPanelRef.current?.expand()}
            style={{ alignSelf: 'flex-start', marginLeft: 4 }}
          >
            <IconChevronLeft size={iconSizes.sm} stroke={1.5} />
          </IconButton>
        </Tooltip>
      )}
    </div>
  )
}

export default ResizableSplit
